import { useEffect, useMemo, useRef, useState } from "react";
import {
  Button,
  Card,
  Form,
  OverlayTrigger,
  Table,
  Tooltip,
} from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSpinner,
  faTriangleExclamation,
  faTrash,
  faCheck,
  faFileExport,
  faBriefcase,
  faUmbrellaBeach,
  faNoteSticky,
} from "@fortawesome/free-solid-svg-icons";
import { faNoteSticky as faNoteStickyRegular } from "@fortawesome/free-regular-svg-icons";
import { useAuth } from "../../../providers/authprovider";
import WorkTimeNoteModal from "./WorkTimeNoteModal";
import {
  formatDateInput,
  formatDurationFromSeconds,
  formatSignedDurationFromSeconds,
  formatTimeInput,
  getDurationSeconds,
  normalizeTimeValue,
  toSeconds,
} from "../utils/helpers";
import DeleteWorkTimeRowModal from "./DeleteWorkTimeModal";

function getSavingEntry(saving, rowId) {
  return saving.find((item) => item.id === rowId) || null;
}

export default function WorkTimeUsersTable({
  group,
  onChangeRow,
  onDeleteRow,
  onApproveRows,
  onExportRows,
  onSaveNote,
  onApproveNote,
  onDeleteNote,
  canEdit,
}) {
  const { user } = useAuth();

  // Notatkę tworzy, edytuje i usuwa wyłącznie właściciel wiersza. Ktoś z prawem
  // edycji czasu pracy może cudzą notatkę tylko zatwierdzić lub cofnąć zatwierdzenie.
  const canManageNote = String(group.userId) === String(user?.id);
  const canApproveNote = canEdit;

  const [localRows, setLocalRows] = useState(group.rows || []);
  const [saving, setSaving] = useState([]);
  const [deleteModal, setDeleteModal] = useState({
    show: false,
    row: null,
  });
  const [noteModal, setNoteModal] = useState({
    show: false,
    rowId: null,
  });
  const [approveLoading, setApproveLoading] = useState(false);
  const [approveError, setApproveError] = useState("");

  const savingRef = useRef([]);
  const timersRef = useRef({});

  const setSavingState = (updater) => {
    setSaving((prev) => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      savingRef.current = next;
      return next;
    });
  };

  useEffect(() => {
    setLocalRows(group.rows || []);
  }, [group.rows]);

  useEffect(() => {
    return () => {
      Object.values(timersRef.current).forEach((timerId) => {
        clearTimeout(timerId);
      });
    };
  }, []);

  const scheduleSave = (rowId) => {
    if (timersRef.current[rowId]) {
      clearTimeout(timersRef.current[rowId]);
    }

    timersRef.current[rowId] = setTimeout(() => {
      flushRow(rowId);
    }, 1000);
  };

  const flushRow = async (rowId) => {
    const entry = getSavingEntry(savingRef.current, rowId);

    if (!entry || !entry.patch || Object.keys(entry.patch).length === 0) {
      return;
    }

    const sentPatch = { ...entry.patch };
    const sentRevision = entry.revision;

    setSavingState((prev) =>
      prev.map((item) =>
        item.id === rowId ? { ...item, state: "loading", error: null } : item,
      ),
    );

    try {
      const result = await onChangeRow?.(rowId, sentPatch);
      const currentEntry = getSavingEntry(savingRef.current, rowId);

      if (!currentEntry) {
        return;
      }

      const hasNewerChanges = currentEntry.revision !== sentRevision;

      if (!result?.success) {
        const errorMessage =
          result?.error || "Nie udało się zapisać zmian w wierszu.";

        if (hasNewerChanges) {
          setSavingState((prev) =>
            prev.map((item) =>
              item.id === rowId
                ? { ...item, state: "pending", error: null }
                : item,
            ),
          );
          scheduleSave(rowId);
          return;
        }

        setSavingState((prev) =>
          prev.map((item) =>
            item.id === rowId
              ? { ...item, state: "error", error: errorMessage }
              : item,
          ),
        );
        return;
      }

      if (hasNewerChanges) {
        setSavingState((prev) =>
          prev.map((item) =>
            item.id === rowId
              ? { ...item, state: "pending", error: null }
              : item,
          ),
        );
        scheduleSave(rowId);
        return;
      }

      setSavingState((prev) => prev.filter((item) => item.id !== rowId));
    } catch (error) {
      const currentEntry = getSavingEntry(savingRef.current, rowId);

      if (!currentEntry) {
        return;
      }

      const hasNewerChanges = currentEntry.revision !== sentRevision;
      const errorMessage =
        error?.message ||
        error?.error ||
        "Nie udało się zapisać zmian w wierszu.";

      if (hasNewerChanges) {
        setSavingState((prev) =>
          prev.map((item) =>
            item.id === rowId
              ? { ...item, state: "pending", error: null }
              : item,
          ),
        );
        scheduleSave(rowId);
        return;
      }

      setSavingState((prev) =>
        prev.map((item) =>
          item.id === rowId
            ? { ...item, state: "error", error: errorMessage }
            : item,
        ),
      );
    }
  };

  const handleRowChange = (row, patch) => {
    setLocalRows((prev) =>
      prev.map((item) => (item.id === row.id ? { ...item, ...patch } : item)),
    );

    setSavingState((prev) => {
      const currentEntry = getSavingEntry(prev, row.id);

      if (!currentEntry) {
        return [
          ...prev,
          {
            id: row.id,
            state: "pending",
            error: null,
            patch: { ...patch },
            revision: 1,
          },
        ];
      }

      return prev.map((item) =>
        item.id === row.id
          ? {
              ...item,
              state: "pending",
              error: null,
              patch: {
                ...(item.patch || {}),
                ...patch,
              },
              revision: (item.revision || 0) + 1,
            }
          : item,
      );
    });

    scheduleSave(row.id);
  };

  const handleDateChange = (row, value) => {
    handleRowChange(row, {
      date: value,
    });
  };

  const handleStartTimeChange = (row, value) => {
    handleRowChange(row, {
      start_time: normalizeTimeValue(value),
    });
  };

  const handleEndTimeChange = (row, value) => {
    handleRowChange(row, {
      end_time: value ? normalizeTimeValue(value) : null,
    });
  };

  const handleNormChange = (row, value) => {
    handleRowChange(row, {
      work_norm: normalizeTimeValue(value),
    });
  };

  const handleOpenDeleteModal = (row) => {
    setDeleteModal({
      show: true,
      row,
    });
  };

  const handleCloseDeleteModal = () => {
    setDeleteModal({
      show: false,
      row: null,
    });
  };

  const handleRowDeleted = (rowId) => {
    if (timersRef.current[rowId]) {
      clearTimeout(timersRef.current[rowId]);
      delete timersRef.current[rowId];
    }

    setLocalRows((prev) => prev.filter((item) => item.id !== rowId));
    setSavingState((prev) => prev.filter((item) => item.id !== rowId));
  };

  const handleOpenNoteModal = (row) => {
    setNoteModal({
      show: true,
      rowId: row.id,
    });
  };

  const handleCloseNoteModal = () => {
    setNoteModal({
      show: false,
      rowId: null,
    });
  };

  const handleSaveNote = async (rowId, noteId, data) => {
    const result = await onSaveNote?.(rowId, noteId, data);

    if (result?.success) {
      setLocalRows((prev) =>
        prev.map((item) =>
          item.id === rowId ? { ...item, worktime_note: result.content } : item,
        ),
      );
    }

    return result;
  };

  const handleApprove = async () => {
    if (approveLoading) return;

    setApproveLoading(true);
    setApproveError("");

    const entryIds = localRows
      .filter((row) => !row.approved)
      .map((row) => row.id);

    const result = await onApproveRows?.(group.userId, entryIds);

    setApproveLoading(false);

    if (!result?.success) {
      setApproveError(result?.error || "Nie udało się zatwierdzić rekordów.");
      return;
    }

    setLocalRows((prev) =>
      prev.map((row) =>
        entryIds.includes(row.id) ? { ...row, approved: true } : row,
      ),
    );
  };

  const handleExport = async () => {
    await onExportRows?.(group.userId, localRows);
  };

  const totalNormSeconds = useMemo(() => {
    return localRows.reduce((sum, row) => {
      return sum + toSeconds(row.work_norm || "08:00:00");
    }, 0);
  }, [localRows]);

  const totalWorkSeconds = useMemo(() => {
    return localRows.reduce((sum, row) => {
      return sum + getDurationSeconds(row.start_time, row.end_time);
    }, 0);
  }, [localRows]);

  const overtimeSeconds = totalWorkSeconds - totalNormSeconds;

  const hasPendingApproval = localRows.some((row) => !row.approved);
  const allApproved =
    localRows.length > 0 && localRows.every((row) => !!row.approved);

  return (
    <>
      <Card>
        <Card.Body>
          <div className="mb-3">
            <h5 className="mb-0">{group.userName}</h5>
            {group.userLogin ? (
              <div className="text-muted small">{group.userLogin}</div>
            ) : null}
          </div>

          <Table
            striped
            bordered
            hover
            responsive
            className="mb-0 align-middle"
          >
            <thead>
              <tr>
                <th style={{ width: 44 }}></th>
                <th style={{ width: 44 }}>Notatka</th>
                <th>Data</th>
                <th>Start</th>
                <th>Koniec</th>
                <th>Norma</th>
                <th>Czas pracy</th>
                <th>Typ</th>
                {canEdit ? <th style={{ width: 64 }}></th> : null}
              </tr>
            </thead>
            <tbody>
              {localRows.map((row) => {
                const durationSeconds = getDurationSeconds(
                  row.start_time,
                  row.end_time,
                );

                const normSeconds = toSeconds(row.work_norm || "08:00:00");
                const diffSeconds = durationSeconds - normSeconds;

                const savingEntry = getSavingEntry(saving, row.id);
                const isLoading = savingEntry?.state === "loading";
                const isError = savingEntry?.state === "error";
                const isPending = savingEntry?.state === "pending";
                const approved = !!row.approved;

                let durationClass = "";
                let tooltipText = "";

                if (diffSeconds > 0) {
                  durationClass = "text-success fw-semibold";
                  tooltipText = `Nadgodziny: +${formatDurationFromSeconds(diffSeconds)}`;
                } else if (diffSeconds < 0) {
                  durationClass = "text-danger fw-semibold";
                  tooltipText = `Braki: -${formatDurationFromSeconds(Math.abs(diffSeconds))}`;
                }

                return (
                  <tr key={row.id}>
                    <td className="text-center">
                      {isLoading ? (
                        <FontAwesomeIcon icon={faSpinner} spin />
                      ) : null}

                      {!isLoading && isPending ? (
                        <span
                          className="text-muted"
                          title="Zmiany oczekują na zapis"
                        >
                          …
                        </span>
                      ) : null}

                      {isError ? (
                        <OverlayTrigger
                          placement="top"
                          overlay={
                            <Tooltip id={`worktime-row-error-${row.id}`}>
                              {savingEntry.error}
                            </Tooltip>
                          }
                        >
                          <span style={statusIconWrapperStyle}>
                            <FontAwesomeIcon
                              icon={faTriangleExclamation}
                              className="text-warning"
                            />
                          </span>
                        </OverlayTrigger>
                      ) : null}
                    </td>

                    <td className="text-center">
                      {/* pusty kafelek notatki ma sens tylko w swoim wierszu —
                          w cudzych pokazujemy ikonę dopiero, gdy notatka istnieje */}
                      {row.worktime_note || canManageNote ? (
                        <OverlayTrigger
                          placement="top"
                          overlay={
                            <Tooltip id={`worktime-note-${row.id}`}>
                              {getNoteTooltipText(row.worktime_note)}
                            </Tooltip>
                          }
                        >
                          <Button
                            variant="link"
                            size="sm"
                            className={`p-0 ${getNoteClassName(row.worktime_note)}`}
                            onClick={() => handleOpenNoteModal(row)}
                          >
                            <FontAwesomeIcon
                              icon={
                                row.worktime_note
                                  ? faNoteSticky
                                  : faNoteStickyRegular
                              }
                            />
                          </Button>
                        </OverlayTrigger>
                      ) : null}
                    </td>

                    <td style={{ minWidth: 160 }}>
                      <Form.Control
                        type="date"
                        disabled={!canEdit || approved}
                        style={cellInputStyle}
                        value={formatDateInput(row.date)}
                        onChange={(e) => handleDateChange(row, e.target.value)}
                      />
                    </td>

                    <td style={{ minWidth: 140 }}>
                      <Form.Control
                        type="time"
                        disabled={!canEdit || approved}
                        style={cellInputStyle}
                        step="1"
                        value={formatTimeInput(row.start_time)}
                        onChange={(e) =>
                          handleStartTimeChange(row, e.target.value)
                        }
                      />
                    </td>

                    <td style={{ minWidth: 140 }}>
                      <Form.Control
                        type="time"
                        style={cellInputStyle}
                        disabled={!canEdit || approved}
                        step="1"
                        value={formatTimeInput(row.end_time)}
                        onChange={(e) =>
                          handleEndTimeChange(row, e.target.value)
                        }
                      />
                    </td>

                    <td style={{ minWidth: 140 }}>
                      <Form.Control
                        type="time"
                        step="1"
                        style={cellInputStyle}
                        disabled={!canEdit || approved}
                        value={formatTimeInput(row.work_norm || "08:00:00")}
                        onChange={(e) => handleNormChange(row, e.target.value)}
                      />
                    </td>

                    <td>
                      {tooltipText ? (
                        <OverlayTrigger
                          placement="top"
                          overlay={
                            <Tooltip id={`worktime-diff-${row.id}`}>
                              {tooltipText}
                            </Tooltip>
                          }
                        >
                          <span className={durationClass}>
                            {formatDurationFromSeconds(durationSeconds)}
                          </span>
                        </OverlayTrigger>
                      ) : (
                        <span>
                          {formatDurationFromSeconds(durationSeconds)}
                        </span>
                      )}
                    </td>

                    <td className="text-center">
                      <OverlayTrigger
                        placement="top"
                        overlay={
                          <Tooltip id={`worktime-type-${row.id}`}>
                            {getWorkTimeTypeTooltipContent(row)}
                          </Tooltip>
                        }
                      >
                        <span style={statusIconWrapperStyle}>
                          <FontAwesomeIcon icon={getWorkTimeTypeIcon(row.type)} />
                        </span>
                      </OverlayTrigger>
                    </td>

                    {canEdit ? (
                      <td className="text-center">
                        <Button
                          variant="outline-danger"
                          disabled={approved}
                          size="sm"
                          onClick={() => handleOpenDeleteModal(row)}
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </Button>
                      </td>
                    ) : null}
                  </tr>
                );
              })}

              <tr className="fw-semibold">
                <td></td>
                <td></td>
                <td colSpan={3}>Suma</td>
                <td>{formatDurationFromSeconds(totalNormSeconds)}</td>
                <td>{formatDurationFromSeconds(totalWorkSeconds)}</td>
                <td></td>
                {canEdit ? <td></td> : null}
              </tr>

              <tr className="fw-semibold">
                <td></td>
                <td></td>
                <td colSpan={4}>Nadgodziny / różnica</td>
                <td>{formatSignedDurationFromSeconds(overtimeSeconds)}</td>
                <td></td>
                {canEdit ? <td></td> : null}
              </tr>
            </tbody>
          </Table>

          <div className="d-flex gap-2 justify-content-end mt-3">
            {canEdit && (
              <Button
                variant={
                  hasPendingApproval ? "outline-success" : "outline-secondary"
                }
                onClick={handleApprove}
                disabled={!canEdit || !hasPendingApproval || approveLoading}
              >
                <FontAwesomeIcon
                  icon={approveLoading ? faSpinner : faCheck}
                  spin={approveLoading}
                />{" "}
                {hasPendingApproval ? "Zatwierdź" : "Zatwierdzone"}
              </Button>
            )}

            <Button
              variant={allApproved ? "outline-success" : "outline-secondary"}
              onClick={handleExport}
              disabled={!allApproved}
            >
              <FontAwesomeIcon icon={faFileExport} /> Eksportuj
            </Button>
          </div>

          {approveError ? (
            <div className="text-danger small mt-2">{approveError}</div>
          ) : null}
        </Card.Body>
      </Card>

      <DeleteWorkTimeRowModal
        show={deleteModal.show}
        row={deleteModal.row}
        onHide={handleCloseDeleteModal}
        onDeleteRow={onDeleteRow}
        onRowDeleted={handleRowDeleted}
      />

      <WorkTimeNoteModal
        show={noteModal.show}
        row={localRows.find((item) => item.id === noteModal.rowId) || null}
        canManageNote={canManageNote}
        canApproveNote={canApproveNote}
        onHide={handleCloseNoteModal}
        onSaveNote={handleSaveNote}
        onApproveNote={onApproveNote}
        onDeleteNote={onDeleteNote}
      />
    </>
  );
}

function getNoteClassName(note) {
  if (!note) return "text-muted";

  return note.approved ? "text-success" : "text-warning";
}

function getNoteTooltipText(note) {
  if (!note) return "Brak notatki";

  const status = note.approved
    ? "Notatka zatwierdzona"
    : "Notatka oczekuje na zatwierdzenie";

  return (
    <>
      <div>{status}</div>
      {note.approved && note.approved_user_name ? (
        <div>Zatwierdził: {note.approved_user_name}</div>
      ) : null}
    </>
  );
}

const cellInputStyle = {
  border: "none",
  padding: 0,
  background: "transparent",
  boxShadow: "none",
  height: "auto",
};

const statusIconWrapperStyle = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "help",
};

function getWorkTimeTypeIcon(type) {
  if (type === "vacation") return faUmbrellaBeach;

  return faBriefcase;
}

function getWorkTimeTypeLabel(type) {
  if (type === "vacation") return "Urlop";

  return "Praca";
}

function getWorkTimeTypeTooltipContent(row) {
  if (row.type !== "vacation" || row.customEvent === undefined) {
    return getWorkTimeTypeLabel(row.type);
  }

  const { date_from, date_to, value } = row.customEvent;

  return (
    <>
      <div>Urlop</div>
      <div>Od: {formatDateInput(date_from)}</div>
      <div>Do: {formatDateInput(date_to)}</div>
      {value ? <div>Treść: {value}</div> : null}
    </>
  );
}
